// client/src/components/bracket/BracketDiagram.js
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import BracketViewport from './BracketViewport'
import BracketLayer from './BracketLayer'

/**
 * BracketDiagram
 * Renders a playoff bracket using backend-provided data inside a pan/zoom viewport.
 *
 * Props:
 * - bracket: { rounds: Array<{ round: number, matches: Array<Match> }> }
 * - height?: number (default 600)
 * - enableFitToWidth?: boolean (default true)
 * - fitPadding?: number (default 24)
 * - className?: string (optional extra class on BracketLayer)
 * - onMatchClick?: (match) => void - callback when a match card is clicked
 * - collapsedRounds?: Set<number> - set of round numbers that are collapsed
 * - onToggleRound?: (roundNumber) => void - callback to toggle round collapse state
 * - simpleScroll?: boolean (default false) - if true, disables viewport and uses simple scrolling
 */
export default function BracketDiagram({
  bracket,
  height = 600,
  enableFitToWidth = true,
  fitPadding = 24,
  className = '',
  onMatchClick,
  collapsedRounds = new Set(),
  onToggleRound,
  simpleScroll = false,
}) {
  // Refs to measure layout and match card positions
  const layerRef = useRef(null)
  const matchRefs = useRef({})
  const [hoverKey, setHoverKey] = useState(null)

  // Normalize rounds to a stable array so hooks can run unconditionally
  const rounds = useMemo(
    () => (Array.isArray(bracket?.rounds) ? bracket.rounds : []),
    [bracket],
  )
  const hasRounds = rounds.length > 0

  // Helper to build a stable key for a match
  const matchKey = useCallback(
    (roundIndex, matchIndex, match) =>
      String(match?.id ?? `${roundIndex}-${matchIndex}`),
    [],
  )

  // Compute mapping to next-round match index/position if possible
  const getNextRoundAndIndex = useCallback(
    (roundIdx, matchIdx, match) => {
      const nextRoundObj = rounds[roundIdx + 1]
      if (!nextRoundObj) return null

      // Prefer explicit mapping from notes if present
      let nextPosFromNotes = null
      try {
        if (match?.notes && typeof match.notes === 'string') {
          const n = JSON.parse(match.notes)
          nextPosFromNotes = n?.next_match_position ?? n?.next_position ?? null
        } else if (match?.notes && typeof match.notes === 'object') {
          nextPosFromNotes =
            match.notes.next_match_position ?? match.notes.next_position ?? null
        }
      } catch (err) {
        console.error('Failed to parse match.notes JSON:', err, match?.notes)
      }

      if (Number.isFinite(nextPosFromNotes)) {
        const idx = Math.max(
          0,
          Math.min(
            nextRoundObj.matches.length - 1,
            Number(nextPosFromNotes) - 1,
          ),
        )
        return { nextRoundIdx: roundIdx + 1, nextMatchIdx: idx }
      }

      // Fallback: proper tournament bracket pairing
      // In a standard bracket: matches 1,2 → match 1; matches 3,4 → match 2; etc.
      const currentPos = Number.isFinite(match?.position)
        ? Number(match.position)
        : matchIdx + 1

      // Standard tournament bracket mapping: pairs of matches feed into next round
      // Matches 1,2 → index 0; Matches 3,4 → index 1; etc.
      const nextIdx = Math.floor((currentPos - 1) / 2)
      const clampedNextIdx = Math.max(
        0,
        Math.min(nextRoundObj.matches.length - 1, nextIdx),
      )

      return { nextRoundIdx: roundIdx + 1, nextMatchIdx: clampedNextIdx }
    },
    [rounds],
  )

  // Connections state built from measured positions
  const [connections, setConnections] = useState([])

  // Measure and rebuild connector paths
  const measureConnections = useCallback(() => {
    const container = layerRef.current
    if (!container) return

    const containerRect = container.getBoundingClientRect()
    const conns = []

    rounds.forEach((round, rIdx) => {
      if (rIdx >= rounds.length - 1) return
      ;(round.matches || []).forEach((m, mIdx) => {
        const srcKey = matchKey(rIdx, mIdx, m)
        const srcEl = matchRefs.current[srcKey]
        const mapping = getNextRoundAndIndex(rIdx, mIdx, m)
        if (!srcEl || !mapping) return

        const tgtMatch =
          rounds[mapping.nextRoundIdx]?.matches?.[mapping.nextMatchIdx]
        const tgtKey =
          tgtMatch &&
          matchKey(mapping.nextRoundIdx, mapping.nextMatchIdx, tgtMatch)
        const tgtEl = tgtKey && matchRefs.current[tgtKey]
        if (!tgtEl) return

        const s = srcEl.getBoundingClientRect()
        const t = tgtEl.getBoundingClientRect()

        const x1 = s.right - containerRect.left
        const y1 = s.top + s.height / 2 - containerRect.top
        const x2 = t.left - containerRect.left
        const y2 = t.top + t.height / 2 - containerRect.top

        // Create traditional bracket connectors (horizontal-vertical-horizontal)
        // This matches standard tournament bracket appearance
        const xMid = x1 + (x2 - x1) * 0.5 // Midpoint for traditional right-angle turn

        conns.push({
          x1,
          y1,
          x2,
          y2,
          xMid,
          yMid: y2, // Use target y-coordinate for traditional straight lines
          sourceKey: srcKey,
          targetKey: tgtKey,
        })
      })
    })

    setConnections(conns)
  }, [rounds, matchKey, getNextRoundAndIndex])

  // Recompute connections after render/layout
  useLayoutEffect(() => {
    measureConnections()
  }, [measureConnections])

  // Update on resize
  useEffect(() => {
    if (!layerRef.current) return
    const ro = new ResizeObserver(() => {
      // recompute on resize
      measureConnections()
    })
    ro.observe(layerRef.current)
    return () => ro.disconnect()
  }, [measureConnections])

  // Utility render of overlay SVG - recomputes when connections change to reflect bracket updates
  const overlaySize = useMemo(() => {
    const el = layerRef.current
    return {
      width: el ? el.scrollWidth || el.clientWidth || 0 : 0,
      height: el ? el.scrollHeight || el.clientHeight || 0 : 0,
    }
    // Recalculate overlay size when bracket layout changes
  }, [])

  if (!hasRounds) {
    return null
  }

  // Bracket content (same for both modes)
  const bracketContent = (
    <BracketLayer
      ref={layerRef}
      className={`bracket-layer--rounds bracket-diagram ${className}`.trim()}
    >
      {/* SVG overlay for connectors */}
      <svg
        className="bracket-diagram__overlay"
        width={overlaySize.width}
        height={overlaySize.height}
        viewBox={`0 0 ${overlaySize.width} ${overlaySize.height}`}
        aria-hidden="true"
      >
        <defs>
          <marker
            id="bracket-arrow"
            viewBox="0 0 10 10"
            refX="10"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
          </marker>
        </defs>
        {connections.map((c, i) => (
          <g key={i}>
            <path
              className={`conn ${
                hoverKey &&
                (hoverKey === c.sourceKey || hoverKey === c.targetKey)
                  ? 'conn--highlight'
                  : ''
              }`}
              d={`M ${c.x1} ${c.y1} L ${c.xMid} ${c.y1} L ${c.xMid} ${c.y2} L ${c.x2} ${c.y2}`}
              fill="none"
              markerEnd="url(#bracket-arrow)"
            />
            <circle
              className={`conn-endpoint ${
                hoverKey &&
                (hoverKey === c.sourceKey || hoverKey === c.targetKey)
                  ? 'conn-endpoint--highlight'
                  : ''
              }`}
              cx={c.x1}
              cy={c.y1}
              r={3}
            />
            <circle
              className={`conn-endpoint ${
                hoverKey &&
                (hoverKey === c.sourceKey || hoverKey === c.targetKey)
                  ? 'conn-endpoint--highlight'
                  : ''
              }`}
              cx={c.x2}
              cy={c.y2}
              r={3}
            />
          </g>
        ))}
      </svg>

      {rounds.map((round, roundIndex) => {
        const title =
          round.round === rounds.length
            ? 'Final'
            : round.round === rounds.length - 1
              ? 'Semifinal'
              : round.round === rounds.length - 2
                ? 'Quarterfinal'
                : `Round ${round.round}`

        const isCollapsed = collapsedRounds.has(round.round)
        const matchCount = (round.matches || []).length

        return (
          <div key={round.round} className="round-column">
            <div
              className={`round-header fw-semibold text-muted small d-flex align-items-center gap-2 ${
                onToggleRound ? 'round-header--collapsible' : ''
              }`}
              onClick={() => onToggleRound && onToggleRound(round.round)}
              role={onToggleRound ? 'button' : undefined}
              tabIndex={onToggleRound ? 0 : undefined}
              onKeyDown={(e) => {
                if (onToggleRound && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  onToggleRound(round.round)
                }
              }}
              aria-expanded={onToggleRound ? !isCollapsed : undefined}
            >
              {onToggleRound && (
                <span
                  className={`round-collapse-icon ${
                    isCollapsed ? 'collapsed' : ''
                  }`}
                  aria-hidden="true"
                >
                  ▼
                </span>
              )}
              <span>{title}</span>
              <span className="badge bg-light text-muted ms-1">
                {matchCount}
              </span>
            </div>
            {isCollapsed ? (
              <div className="text-muted small text-center py-3 collapsed-round-placeholder">
                {matchCount} match{matchCount !== 1 ? 'es' : ''} hidden
              </div>
            ) : (round.matches || []).length === 0 ? (
              <div className="text-muted">No matches</div>
            ) : (
              round.matches.map((match, matchIndex) => {
                // Extract seed information from match columns
                const homeSeed = match.home_seed ?? null
                const awaySeed = match.away_seed ?? null

                const homePlayerName =
                  match.home_first_name && match.home_last_name
                    ? `${match.home_first_name} ${match.home_last_name}`.trim()
                    : match.home_player_id
                      ? 'Player ' + match.home_player_id
                      : 'TBD'

                const awayPlayerName =
                  match.away_first_name && match.away_last_name
                    ? `${match.away_first_name} ${match.away_last_name}`.trim()
                    : match.away_player_id
                      ? 'Player ' + match.away_player_id
                      : 'TBD'

                const isCompleted = [
                  'normal',
                  'home_retired',
                  'away_retired',
                  'i_retired',
                  'opponent_retired',
                ].includes(match.status)
                const isReady =
                  match.status === 'created' &&
                  match.home_player_id &&
                  match.away_player_id

                // Determine if match is clickable (ready for score entry)
                const isClickable = onMatchClick && isReady && !isCompleted

                // Determine winner for completed matches
                let homeIsWinner = false
                let awayIsWinner = false
                if (isCompleted) {
                  if (
                    match.status === 'home_retired' ||
                    match.status === 'i_retired'
                  ) {
                    awayIsWinner = true
                  } else if (
                    match.status === 'away_retired' ||
                    match.status === 'opponent_retired'
                  ) {
                    homeIsWinner = true
                  } else if (match.status === 'normal' && match.set_scores) {
                    try {
                      const setScores = Array.isArray(match.set_scores)
                        ? match.set_scores
                        : JSON.parse(match.set_scores || '[]')
                      let homeSets = 0
                      let awaySets = 0
                      setScores.forEach((set) => {
                        if (set.home_games > set.away_games) homeSets++
                        else if (set.away_games > set.home_games) awaySets++
                      })
                      homeIsWinner = homeSets > awaySets
                      awayIsWinner = awaySets > homeSets
                    } catch (e) {
                      // Log parse errors for debugging purposes
                      console.error(
                        'Failed to parse set_scores for match:',
                        match,
                        'Error:',
                        e,
                        'Raw set_scores:',
                        match.set_scores,
                      )
                    }
                  }
                }

                const statusBadgeClass = (() => {
                  if (isCompleted) return 'bg-success'
                  if (match.status === 'scheduled') return 'bg-info'
                  if (match.status === 'created') return 'bg-primary'
                  if (match.status === 'bye') return 'bg-warning'
                  return 'bg-secondary'
                })()

                const statusText = (() => {
                  if (isCompleted) return 'Complete'
                  if (match.status === 'scheduled') return 'Scheduled'
                  if (match.status === 'created') return 'TBD'
                  if (match.status === 'bye') return 'Bye'
                  return 'Pending'
                })()

                const key = matchKey(roundIndex, matchIndex, match)

                return (
                  <div
                    key={key}
                    data-testid={`playoff-match-card-${match.id}`}
                    ref={(el) => {
                      if (el) matchRefs.current[key] = el
                      else delete matchRefs.current[key]
                    }}
                    onMouseEnter={() => setHoverKey(key)}
                    onMouseLeave={() => setHoverKey(null)}
                    onClick={() => isClickable && onMatchClick(match)}
                    onKeyDown={(e) => {
                      if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        onMatchClick(match)
                      }
                    }}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    className={`card shadow-sm match-card ${
                      isCompleted
                        ? 'border-success'
                        : isReady
                          ? 'border-primary'
                          : 'border-light'
                    } ${isClickable ? 'match-card--clickable' : ''}`}
                    style={{ position: 'relative', zIndex: 2 }}
                    aria-label={
                      isClickable
                        ? `Click to report score for match ${
                            match.position || matchIndex + 1
                          }`
                        : undefined
                    }
                  >
                    <div className="card-body p-2">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="text-muted small">
                          Match {match.position || matchIndex + 1}
                        </div>
                        <span className={`badge ${statusBadgeClass}`}>
                          {statusText}
                        </span>
                      </div>
                      <div className="d-flex flex-column gap-1">
                        <div
                          className={`d-flex justify-content-between ${
                            homeIsWinner ? 'fw-semibold text-success' : ''
                          }`}
                        >
                          <span>
                            <span className="badge bg-light text-dark me-2">
                              {homeSeed || '?'}
                            </span>
                            <span>{homePlayerName}</span>
                          </span>
                        </div>
                        <div
                          className={`d-flex justify-content-between ${
                            awayIsWinner ? 'fw-semibold text-success' : ''
                          }`}
                        >
                          <span>
                            <span className="badge bg-light text-dark me-2">
                              {awaySeed || '?'}
                            </span>
                            <span>{awayPlayerName}</span>
                          </span>
                        </div>
                      </div>
                      {isCompleted && match.set_scores && (
                        <div className="text-center mt-2 small fw-bold border-top pt-1">
                          {(() => {
                            try {
                              const scores = Array.isArray(match.set_scores)
                                ? match.set_scores
                                : JSON.parse(match.set_scores || '[]')
                              return scores
                                .map((s) => `${s.home_games}-${s.away_games}`)
                                .join(', ')
                            } catch {
                              return ''
                            }
                          })()}
                        </div>
                      )}
                      {isClickable && (
                        <div className="text-center mt-2">
                          <small className="text-primary">
                            <i className="fas fa-edit me-1"></i>
                            Click to report score
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        )
      })}
    </BracketLayer>
  )

  // Return with or without viewport wrapper
  if (simpleScroll) {
    return bracketContent
  }

  return (
    <BracketViewport
      enableFitToWidth={enableFitToWidth}
      fitPadding={fitPadding}
      style={{ height }}
    >
      {bracketContent}
    </BracketViewport>
  )
}
